import config from 'config/config';
import { DemoApiResponse, DemoMatch } from 'types/demos';
import { createAxiosInstance } from 'utils/axiosConfig';

const extractMatches = (payload: DemoApiResponse | DemoMatch[]): DemoMatch[] => {
    if (Array.isArray(payload)) {
        return payload;
    }

    return payload.matches ?? [];
};

// Configure axios with timeout for all demo service requests
const axiosInstance = createAxiosInstance({
    timeout: 10000, // 10 second timeout
});

class DemoMatchesService {
    host = config.API_BASE_URL;
    // API base path for public read operations – matches app.js route: app.use('/api/v1/demos', demosRouter);
    apiPath = '/v1/demos';
    // Admin API path for write operations (create, update, delete) – requires authentication
    adminApiPath = '/v1/intra/demos';
    // Fallback already configured in config.ts (typically /mock/demo-matches.json)


    /**
     * Utility to unify URL joining (prevents double slashes)
     * 
     * Note: This helper does not handle:
     * - Protocol-relative URLs (e.g., //path)
     * - Query strings (e.g., path?query)
     * - URL fragments (e.g., path#fragment)
     * Use only for basic path joining of API endpoints.
     */
    private joinUrl(...parts: string[]): string {
        return parts
            .map((part, i) => {
                if (i === 0) return part.replace(/\/+$/, '');
                return part.replace(/^\/+|\/+$/g, '');
            })
            .filter(Boolean)
            .join('/');
    }

    /**
     * Build download URL for demo file using match ID
     */
    buildDemoDownloadUrl(matchId: string): string {
        const encoded = encodeURIComponent(matchId);
        if (this.host) {
            // Unified url joining
            return this.joinUrl(this.host, this.apiPath, 'd', encoded);
        }
        // Fallback to relative API path
        return this.joinUrl(this.apiPath, 'd', encoded);
    }

    /**
     * Fetch all demo matches from API or fallback to mock data
     */
    async fetchDemoMatches(): Promise<DemoMatch[]> {
        if (!this.host) {
            throw new Error('API host not configured for fetching matches');
        }

        const apiUrl = this.joinUrl(this.host, this.apiPath, 'matches');

        const candidates = [apiUrl].filter(
            (url, index, arr) => url && arr.indexOf(url) === index
        );

        for (const url of candidates) {
            if (!url) continue;

            try {
                const response = await axiosInstance.get<DemoApiResponse | DemoMatch[]>(url);
                const matches = extractMatches(response.data);
                if (!matches || matches.length === 0) {
                    continue;
                }
                return matches;
            } catch (error) {
                console.warn(`[demoMatchesService] Failed to load demo metadata from ${url}`, error);
            }
        }

        throw new Error('Demo metadata is not available.');
    }

    /**
     * Fetch all demo matches with fileName (requires authentication)
     */
    async fetchDemoMatchesAuthenticated(token: string, tournamentId?: string): Promise<DemoMatch[]> {
        if (!this.host) {
            throw new Error('API host not configured for fetching authenticated matches');
        }

        const apiUrl = this.joinUrl(this.host, this.adminApiPath, 'matches');
        const params = tournamentId ? { tournamentId } : {};
        
        const response = await axiosInstance.get<{ data: DemoMatch[] }>(apiUrl, {
            headers: {
                Authorization: `Bearer ${token}`
            },
            params
        });
        
        return response.data.data;
    }

    /**
     * Fetch a single demo match by ID
     */
    async fetchDemoMatchById(id: string): Promise<DemoMatch> {
        if (!this.host) {
            throw new Error('API host not configured for fetching match by ID');
        }

        const apiUrl = this.joinUrl(this.host, this.apiPath, 'matches', id);
        const response = await axiosInstance.get<DemoMatch>(apiUrl);
        return response.data;
    }

    /**
     * Create a new demo match (requires authentication)
     */
    async createDemoMatch(matchData: Partial<DemoMatch>, token: string): Promise<DemoMatch> {
        if (!this.host) {
            throw new Error('API host not configured for creating match');
        }

        const apiUrl = this.joinUrl(this.host, this.adminApiPath, 'matches');
        const response = await axiosInstance.post<{ data: DemoMatch }>(apiUrl, matchData, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data.data;
    }

    /**
     * Update an existing demo match (requires authentication)
     */
    async updateDemoMatch(id: string, matchData: Partial<DemoMatch>, token: string): Promise<DemoMatch> {
        if (!this.host) {
            throw new Error('API host not configured for updating match');
        }

        const apiUrl = this.joinUrl(this.host, this.adminApiPath, 'matches', id);
        const response = await axiosInstance.put<{ data: DemoMatch }>(apiUrl, matchData, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data.data;
    }

    /**
     * Delete a demo match (requires authentication)
     */
    async deleteDemoMatch(id: string, token: string): Promise<void> {
        if (!this.host) {
            throw new Error('API host not configured for deleting match');
        }

        const apiUrl = this.joinUrl(this.host, this.adminApiPath, 'matches', id);
        await axiosInstance.delete(apiUrl, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
    }
}

const demoMatchesService = new DemoMatchesService();

export default demoMatchesService;

// Export functions for backward compatibility
export const buildDemoDownloadUrl = (matchId: string): string => {
    return demoMatchesService.buildDemoDownloadUrl(matchId);
};

export const fetchDemoMatches = (): Promise<DemoMatch[]> => {
    return demoMatchesService.fetchDemoMatches();
};
